import { Box, Button, Container, Flex, HStack, Text } from "@chakra-ui/react"
import { Link, useRouterState } from "@tanstack/react-router"
import { memo, useCallback } from "react"
import { FiDroplet } from "react-icons/fi"

interface PublicHeaderProps {
  /**
   * Hide the Log In and Sign Up buttons.
   * Useful when displaying the header on login/signup pages.
   */
  hideAuthButtons?: boolean
}

/**
 * Header component for public (unauthenticated) pages.
 * Displays the Dory logo, navigation link, and auth buttons.
 */
export const PublicHeader = memo(
  ({ hideAuthButtons = false }: PublicHeaderProps) => {
    const routerState = useRouterState()
    const location = routerState.location

    const isActive = useCallback(
      (path: string) => {
        if (
          path === "/browse-classes" &&
          location.pathname === "/browse-classes"
        )
          return true
        if (path !== "/browse-classes" && location.pathname.startsWith(path))
          return true
        return false
      },
      [location.pathname],
    )

    return (
      <Box
        as="header"
        position="sticky"
        top={0}
        zIndex={100}
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Container maxW="7xl" px={{ base: 3, md: 4 }}>
          <Flex
            h={{ base: 14, md: 16 }}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Logo */}
            <Link to="/browse-classes">
              <HStack gap={2} cursor="pointer">
                <Box color="blue.500">
                  <FiDroplet size={28} aria-hidden="true" />
                </Box>
                <Text
                  fontSize={{ base: "md", md: "xl" }}
                  fontWeight="bold"
                  color="blue.600"
                >
                  Dory Swim School
                </Text>
              </HStack>
            </Link>

            {/* Navigation Links + Auth */}
            <HStack gap={{ base: 2, md: 4 }}>
              <Link to="/browse-classes">
                <Button
                  variant={isActive("/browse-classes") ? "solid" : "ghost"}
                  colorPalette={isActive("/browse-classes") ? "blue" : "gray"}
                  size="sm"
                  minH="44px"
                  aria-current={
                    isActive("/browse-classes") ? "page" : undefined
                  }
                >
                  Browse Classes
                </Button>
              </Link>

              {/* Auth Buttons */}
              {!hideAuthButtons && (
                <>
                  <Link to="/login">
                    <Button
                      variant="outline"
                      colorPalette="blue"
                      size="sm"
                      minH="44px"
                    >
                      Log In
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button
                      variant="solid"
                      colorPalette="blue"
                      size="sm"
                      minH="44px"
                      display={{ base: "none", sm: "inline-flex" }}
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </HStack>
          </Flex>
        </Container>
      </Box>
    )
  },
)

PublicHeader.displayName = "PublicHeader"

export default PublicHeader
